import { type Theme } from "@mui/material";
import { type Components } from "@mui/material/styles";

import { applyPlatformAwareInteractionStyles } from "../utils/applyPlatformAwareInteractionStyles";

declare module "@mui/material/IconButton" {
  interface IconButtonPropsSizeOverrides {
    xSmall: true;
  }

  interface IconButtonOwnProps {
    /**
     * Inverts the button colors.
     *
     * Note: this can't be a boolean because MUI passes it down to the HTML element, which doesn't
     * support boolean values and shows errors in the console.
     */
    invert?: "true" | "false";
    /**
     * For outlined buttons, this controls the thickness of the outline.
     *
     * Defaults to "regular"
     */
    "outline-thickness"?: "thick" | "regular";
  }
}

/**
 * We use CSS outline box-shadow instead of border to handle different outline widths.
 * This prevents content shifting that would occur with border when thickness changes on hover.
 */
export const muiIconButtonOverrides: Components<Theme> = {
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI's IconButton has default padding for the icon,
        // but we are using our custom CBHIcon component,
        // so we need to clean that up.
        padding: 0,

        variants: [
          {
            props: { size: "xSmall" },
            style: {
              width: theme.size?.iconButton.xSmall.size,
              height: theme.size?.iconButton.xSmall.size,

              "& svg": {
                width: theme.size?.iconButton.xSmall.iconSize,
                height: theme.size?.iconButton.xSmall.iconSize,
              },
            },
          },
          {
            props: { size: "small" },
            style: {
              width: theme.size?.iconButton.small.size,
              height: theme.size?.iconButton.small.size,

              "& svg": {
                width: theme.size?.iconButton.small.iconSize,
                height: theme.size?.iconButton.small.iconSize,
              },
            },
          },
          {
            props: { size: "medium" },
            style: {
              width: theme.size?.iconButton.medium.size,
              height: theme.size?.iconButton.medium.size,

              "& svg": {
                width: theme.size?.iconButton.medium.iconSize,
                height: theme.size?.iconButton.medium.iconSize,
              },
            },
          },
          {
            props: { size: "large" },
            style: {
              width: theme.size?.iconButton.large.size,
              height: theme.size?.iconButton.large.size,

              "& svg": {
                width: theme.size?.iconButton.large.iconSize,
                height: theme.size?.iconButton.large.iconSize,
              },
            },
          },
        ],
      }),

      // Used for "contained" variant
      colorPrimary: ({ theme }) => ({
        color: theme.palette.text.invertPrimary,
        backgroundColor: theme.palette.primary.main,

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            backgroundColor: theme.palette.primary.dark,
          },
          defaultStyle: {
            backgroundColor: theme.palette.primary.main,
          },
        }),

        "&.Mui-disabled": {
          color: theme.palette.intent?.disabled.text,
          backgroundColor: theme.palette.intent?.disabled.background,
        },

        variants: [
          {
            props: { invert: "true" },
            style: {
              color: theme.palette.icon?.primary,
              backgroundColor: theme.palette.background?.tertiary,

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  backgroundColor: theme.palette.background?.secondary,
                },
                defaultStyle: {
                  backgroundColor: theme.palette.background?.tertiary,
                },
              }),
            },
          },
          {
            props: { "outline-thickness": "thick" },
            style: {
              outlineWidth: theme.borderWidth?.thick,
              outlineOffset: `calc(${theme.borderWidth?.thick} * -1)`,
            },
          },
        ],
      }),

      // Used for "outlined" variant
      colorSecondary: ({ theme }) => ({
        color: theme.palette.icon?.primary,
        backgroundColor: theme.palette.background?.tertiary,
        boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${theme.palette.border!.primary}`,

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${theme.palette.border!.main}`,
            backgroundColor: theme.palette.background?.tertiary,
          },
          defaultStyle: {
            boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${theme.palette.border!.primary}`,
          },
        }),

        "&.Mui-disabled": {
          color: theme.palette.icon?.disabled,
          boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${theme.palette.border!.subtle}`,
          backgroundColor: theme.palette.background?.tertiary,
        },

        variants: [
          {
            props: { "outline-thickness": "thick" },
            style: {
              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.thick} ${
                    theme.palette?.action.active
                  }`,
                },
                defaultStyle: {
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${
                    theme.palette.border!.primary
                  }`,
                },
              }),
            },
          },

          {
            props: { invert: "true" },
            style: {
              color: theme.palette.text?.invertPrimary,
              backgroundColor: "transparent",
              boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${
                theme.palette.border!.primaryInvert
              }`,

              "&.Mui-disabled": {
                color: theme.palette.intent?.disabled.icon,
                boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${
                  theme.palette.border!.primaryInvert
                }`,
                backgroundColor: "transparent",
              },

              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${String(
                    theme.palette.text.invertPrimary
                  )}`,
                  backgroundColor: "transparent",
                },
                defaultStyle: {
                  color: theme.palette.text?.invertPrimary,
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${
                    theme.palette.border!.primaryInvert
                  }`,
                },
              }),
            },
          },
          {
            props: { "outline-thickness": "thick", invert: "true" },
            style: {
              ...applyPlatformAwareInteractionStyles({
                hoverStyle: {
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.thick} ${String(
                    theme.palette.text.invertPrimary
                  )}`,
                },
                defaultStyle: {
                  boxShadow: `inset 0 0 0 ${theme.borderWidth?.regular} ${
                    theme.palette.border!.primaryInvert
                  }`,
                },
              }),
            },
          },
        ],
      }),

      // Used for "ghost" variant
      colorInfo: ({ theme }) => ({
        color: theme.palette.icon?.primary,
        backgroundColor: "transparent",
        boxShadow: "none",

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            backgroundColor: "transparent",
          },
        }),

        "&.Mui-disabled": {
          color: theme.palette.icon?.disabled,
          boxShadow: "none",
          backgroundColor: "transparent",
        },

        variants: [
          {
            props: { invert: "true" },
            style: {
              color: theme.palette.text?.invertPrimary,
            },
          },
        ],
      }),
    },
  },
};
